import { legacy_createStore as createStore, combineReducers, applyMiddleware, compose } from 'redux'
import { thunk } from 'redux-thunk'
import userReducer from './reducers/userReducer'
import uiReducer from './reducers/uiReducer'
import storyReducer from './reducers/storyReducer'
import quizReducer from './reducers/quizReducer'
import questionReducer from './reducers/questionReducer'
import streakReducer from './reducers/streakReducer'
import dashboardReducer from './reducers/dashboardReducer'
import recorderReducer from './reducers/recorderReducer'
import studentCorrectionsReducer from './reducers/studentCorrectionsReducer'

import storage from 'redux-persist/lib/storage'
import { persistReducer, persistStore } from 'redux-persist'
import onBoardingReducer from './reducers/onBoardingReducer'
import wordsReducer from './reducers/wordsReducer'
import pocketReducer from './reducers/pocketReducer'

const persistConfig = {
  key: 'storymaayot',
  storage,
  whitelist: ['currentStory', 'lastStep', 'lastStepEnum', 'storyLevel', 'story_submitted', 'pinyin_settings'],
}

const quizPersistConfig = {
  key: 'quizmaayot',
  storage,
  whitelist: ['quizDetail', 'quizAnswer', 'isExistsAnswer'],
}

const questionPersistConfig = {
  key: 'questionmaayot',
  storage,
  whitelist: ['question', 'questionContent', 'curAnswer', 'answers', 'cursorRefId', 'cursorTs', 'count'],
}

const streakPersistConfig = {
  key: 'streakmaayot',
  storage,
  whitelist: ['current'],
}

const levelPersistConfig = {
  key: 'levelmaayot',
  storage,
  whitelist: ['level'],
}

// v2: the test-first flow persists step keys instead of step numbers; the key
// bump cleanly orphans any half-finished v1 onboarding state.
// v3: `preferenceType` is persisted, and it used to be seeded with the first
// writing option on load — so every browser that ever opened the old build has
// "Simplified Chinese" saved and would keep rendering it as already chosen,
// which is the exact bug the seeding fix removes. Bumping orphans that value
// instead of leaving the fix invisible to everyone who has been here before.
export const ONBOARDING_PERSIST_KEY = 'onboardingmaayot_v3'
export const ONBOARDING_PERSIST_STORAGE_KEY = `persist:${ONBOARDING_PERSIST_KEY}`

const onboardingPersistConfig = {
  key: ONBOARDING_PERSIST_KEY,
  storage,
  whitelist: [
    'languageInfo',
    'is_result',
    'stepper_step',
    'tempUser',
    'userType',
    'learnAboutType',
    'learnAboutTitle',
    'learnAboutAnswer',
    'goal',
    'experience',
    'examBand',
    'preferenceType',
    'placement',
    'placementDone',
    // `group`/`groupReveal` place the user in the flow, so a reload mid-signup
    // resumes on the same screen with the same questions open rather than
    // dropping the user back at the first group.
    'name',
    'group',
    'groupReveal',
  ],
}

const initialState = {}

const reducer = combineReducers({
  user: persistReducer(levelPersistConfig, userReducer),
  UI: uiReducer,
  story: persistReducer(persistConfig, storyReducer),
  quiz: persistReducer(quizPersistConfig, quizReducer),
  question: persistReducer(questionPersistConfig, questionReducer),
  streak: persistReducer(streakPersistConfig, streakReducer),
  dashboard: dashboardReducer,
  onboarding: persistReducer(onboardingPersistConfig, onBoardingReducer),
  recorder: recorderReducer,
  corrections: studentCorrectionsReducer,
  words: wordsReducer,
  pocket: pocketReducer,
})

const isDevelopment = process.env.NODE_ENV !== 'production'

const composeEnhancers = isDevelopment
  ? (typeof window !== 'undefined' && (window as any).__REDUX_DEVTOOLS_EXTENSION_COMPOSE__) || compose
  : compose

const store = createStore(reducer, initialState, composeEnhancers(applyMiddleware(thunk)))

export type RootReducer = ReturnType<typeof reducer>
export type AppDispatch = typeof store.dispatch

export const persistor = persistStore(store)

export default store
